'use client';

import { createClient } from '@/utils/supabase/client';
import { type Provider } from '@supabase/supabase-js';
import { getURL } from '@/utils/helpers';
import { redirectToPath, SignOut } from './server';
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';

export async function handleRequest(
  e: React.FormEvent<HTMLFormElement>,
  requestFunc: (formData: FormData) => Promise<string>,
  router: AppRouterInstance | null = null
): Promise<boolean | void> {
  // Prevent default form submission refresh
  e.preventDefault();

  const formData = new FormData(e.currentTarget);
  const redirectUrl: string = await requestFunc(formData);

  // Custom-scheme callback (native-app OAuth, e.g. the plugin's juce://…):
  // hand it straight to the browser. router.push would try to fetch it as an
  // RSC payload, fail ("Failed to fetch RSC payload… Falling back to browser
  // navigation"), and reach the app only via Next's error fallback — which
  // some embedded webviews don't survive. Paths ("/…") and http(s) URLs keep
  // the existing behavior. replace (not assign) so the sign-in form doesn't
  // stay in history behind the hop — matches the bounce page's behavior.
  if (/^[a-z][a-z0-9+.-]*:/i.test(redirectUrl) && !/^https?:/i.test(redirectUrl)) {
    window.location.replace(redirectUrl);
    return;
  }

  if (router) {
    // If client-side router is provided, use it to redirect
    return router.push(redirectUrl);
  } else {
    // Otherwise, redirect server-side
    return await redirectToPath(redirectUrl);
  }
}

// Sign-out wrapper. Three things must happen in order, and the final hard
// navigation is load-bearing:
//   1. Client-side supabase.signOut() — fires SIGNED_OUT so onAuthStateChange
//      in app/providers.tsx captures auth.signed_out + resets PostHog
//      identity before we leave the page. PostHog's capture uses
//      fetch({ keepalive: true }) so the request survives the hard nav.
//   2. Server action SignOut — clears server-side cookies, returns the
//      redirect URL (with toast params).
//   3. window.location.assign(redirectUrl) — HARD navigation, not
//      router.push. Soft navigation after a sign-out that clears cookies
//      via fetch-not-action skips the RSC cache invalidation that a
//      cookie-changing server-action-based redirect would have triggered,
//      so the destination page renders with stale signed-in state. Hard
//      navigation forces a fresh request that re-reads cookies (now gone)
//      and renders the signed-out tree.
export async function handleSignOut(
  e: React.FormEvent<HTMLFormElement>,
): Promise<void> {
  e.preventDefault();
  // Capture FormData synchronously: React nulls e.currentTarget after the
  // event handler yields, so reading it after `await` throws "Failed to
  // construct 'FormData': parameter 1 is not of type 'HTMLFormElement'".
  const formData = new FormData(e.currentTarget);

  const supabase = createClient();
  await supabase.auth.signOut({ scope: 'local' });

  const redirectUrl = await SignOut(formData);
  window.location.assign(redirectUrl);
}

export async function signInWithOAuth(e: React.FormEvent<HTMLFormElement>) {
  // Prevent default form submission refresh
  e.preventDefault();
  const formData = new FormData(e.currentTarget);
  const provider = String(formData.get('provider')).trim() as Provider;

  // Create client-side supabase client and call signInWithOAuth
  const supabase = createClient();
  const redirectURL = getURL('/auth/callback');
  await supabase.auth.signInWithOAuth({
    provider: provider,
    options: {
      redirectTo: redirectURL,
    },
  });
}
